import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { Box, Card, CardContent, Typography, Grid, List, ListItem, ListItemIcon, ListItemText, Chip, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { ArrowRightOutlined, AlertOutlined, TrophyOutlined } from '@ant-design/icons';
import ChevronRight from '@mui/icons-material/ChevronRight';

const InsightCard = ({ title, children, icon, gradient = 'primary', titleStyle = {} }) => {
  const theme = useTheme();

  const getGradient = (type) => {
    switch (type) {
      case 'warning':
        return 'linear-gradient(135deg, rgba(255,167,38,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)';
      case 'error':
        return 'linear-gradient(135deg, rgba(255,82,82,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)';
      case 'success':
        return 'linear-gradient(135deg, rgba(76,175,80,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)';
      default: // primary
        return 'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)';
    }
  };

  const getIconColor = (type) => {
    switch (type) {
      case 'warning':
        return theme.palette.warning.main;
      case 'error':
        return theme.palette.error.main;
      case 'success':
        return theme.palette.success.main;
      default:
        return theme.palette.primary.main;
    }
  };

  return (
    <motion.div whileHover={{ scale: 1.015 }} transition={{ duration: 0.2 }}>
      <Card
        sx={{
          height: '100%',
          borderRadius: 2,
          position: 'relative',
          isolation: 'isolate',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            padding: '1px',
            borderRadius: 'inherit',
            background: getGradient(gradient),
            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
            zIndex: -1
          },
          boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
          },
          bgcolor: 'background.paper'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            {icon &&
              React.cloneElement(icon, {
                style: {
                  color: getIconColor(gradient),
                  marginRight: '8px',
                  fontSize: '24px'
                }
              })}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: getIconColor(gradient),
                ...titleStyle
              }}
            >
              {title}
            </Typography>
          </Box>
          {children}
        </CardContent>
      </Card>
    </motion.div>
  );
};

const StrategicInsights = () => {
  const theme = useTheme();
  const contentRef = useRef(null);

  // Get data from Redux
  const sbsData = useSelector((state) => state.competitorsReducer.competitorsAnalysisResults?.side_by_side_analysis);

  // Access the analysis object
  const analysis = sbsData?.analysis || {};

  // Get strategic data
  const executiveRecommendations = analysis['10_executive_recommendations'] || [];
  const competitiveWhiteSpaces = analysis['9_strategic_analysis']?.competitive_white_spaces || [];
  const vulnerabilityAnalysis = analysis['9_strategic_analysis']?.vulnerability_analysis || {};
  const competitiveThreats = analysis['9_strategic_analysis']?.competitive_threats || [];

  // Get opportunity score color
  const getOpportunityColor = (score) => {
    if (score >= 8) return theme.palette.success.main;
    if (score >= 5) return theme.palette.success.light;
    return theme.palette.error.light;
  };

  return (
    <Box sx={{ mb: 4 }} ref={contentRef}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 1 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #0070f3 0%, #6743b5 60%, rgba(103,58,183,0.7) 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent'
          }}
        >
          Strategic Insights
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
        Market opportunities, competitive analysis, and executive recommendations
      </Typography>

      <Box
        sx={{
          mb: 2,
          p: 2,
          bgcolor: 'rgba(0, 118, 255, 0.05)',
          borderRadius: 1,
          borderLeft: '4px solid',
          borderColor: 'primary.main'
        }}
      >
        <Typography component="span" variant="body1" sx={{ fontWeight: 700, color: 'primary.main', display: 'block', mb: 1 }}>
          <strong>💡 How to use these insights:</strong>
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          <ChevronRight sx={{ color: 'primary.main', fontSize: 20, mt: 0.3 }} />
          <Typography variant="body1">
            <strong>Leverage competitive white spaces:</strong> Focus on high-opportunity, underserved market needs (8-9/10 scores) to
            differentiate and expand your offering.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          <ChevronRight sx={{ color: 'primary.main', fontSize: 20, mt: 0.3 }} />
          <Typography variant="body1">
            <strong>Monitor threats and vulnerabilities:</strong> Regularly review competitor strengths and your own weak spots to
            proactively defend your market position.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          <ChevronRight sx={{ color: 'primary.main', fontSize: 20, mt: 0.3 }} />
          <Typography variant="body1">
            <strong>Act on executive recommendations:</strong> Use the prioritized action list as a practical roadmap for growth, product,
            and marketing initiatives.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <ChevronRight sx={{ color: 'primary.main', fontSize: 20, mt: 0.3 }} />
          <Typography variant="body1">
            <strong>Prioritize quick wins:</strong> Identify and implement fast-execution opportunities for immediate impact and momentum.
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Executive Recommendations */}
        <Grid item xs={12} md={6}>
          <InsightCard
            title="Suggested Actions"
            icon={<TrophyOutlined />}
            titleStyle={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #2196F3 0%, #3F51B5 80%, #673AB7 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent'
            }}
          >
            <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
              Executive recommendations based on user feedback and market analysis
            </Typography>

            <List sx={{ pl: 0 }}>
              {executiveRecommendations.map((recommendation, index) => (
                <ListItem
                  key={index}
                  sx={{
                    py: 0.75,
                    px: 0,
                    '&:not(:last-child)': {
                      borderBottom: '1px dashed',
                      borderColor: 'divider'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <ArrowRightOutlined style={{ color: theme.palette.primary.main }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography variant="body1">{recommendation}</Typography>} />
                </ListItem>
              ))}
            </List>
          </InsightCard>
        </Grid>

        {/* Competitive White Spaces */}
        <Grid item xs={12} md={6}>
          <InsightCard
            title="Competitive White Spaces"
            icon={null}
            gradient="success"
            titleStyle={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 80%, #1B5E20 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent'
            }}
          >
            <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
              Market needs that are currently unaddressed or underserved by competitors
            </Typography>

            {competitiveWhiteSpaces.map((whiteSpace, index) => (
              <React.Fragment key={index}>
                <Box sx={{ py: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      pt: 1,
                      mb: 1,
                      background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 90%, #1B5E20 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      color: 'transparent'
                    }}
                  >
                    {whiteSpace.market_need}
                  </Typography>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    <Chip
                      label={`Opportunity: ${whiteSpace.opportunity_score}/10`}
                      sx={{
                        bgcolor: getOpportunityColor(whiteSpace.opportunity_score),
                        color: '#fff',
                        fontWeight: 'bold'
                      }}
                      size="small"
                    />
                    <Chip
                      label={`Time to execute: ${whiteSpace.time_to_market}`}
                      variant="outlined"
                      sx={{ borderColor: theme.palette.divider }}
                      size="small"
                    />
                  </Box>

                  <Typography
                    sx={{
                      p: 1.5,
                      bgcolor: 'rgba(76, 175, 80, 0.05)',
                      borderRadius: 1,
                      borderLeft: '3px solid',
                      borderColor: 'success.main',
                      color: 'text.primary'
                    }}
                    component="div"
                  >
                    <Typography variant="body1" component="div" sx={{ color: 'text.primary', p: 0, m: 0 }}>
                      {whiteSpace.opportunity}
                    </Typography>
                    <br />
                    <Typography variant="body1" component="div" sx={{ color: 'text.primary', p: 0, m: 0 }}>
                      <strong>Currently addressed by: </strong>
                      {whiteSpace.addressed_by}
                    </Typography>
                  </Typography>
                </Box>

                {index < competitiveWhiteSpaces.length - 1 && (
                  <Box
                    sx={{
                      height: '2px',
                      mx: 2,
                      mb: 2,
                      background: 'linear-gradient(90deg, rgba(76,175,80,0) 0%, rgba(76,175,80,0.7) 50%, rgba(76,175,80,0) 100%)'
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </InsightCard>
        </Grid>
      </Grid>

      {/* Threats & Vulnerabilities */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: 1,
            background: 'linear-gradient(135deg, #F44336 0%, #6743b5 60%, rgba(103,58,183,0.7) 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent'
          }}
        >
          Threats & Vulnerabilities
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
          Strategic risks and competitive threats to monitor
        </Typography>

        <Grid container spacing={3}>
          {/* Vulnerability Analysis */}
          <Grid item xs={12} md={6}>
            <InsightCard title="Vulnerability Analysis" icon={<AlertOutlined />} gradient="error">
              {vulnerabilityAnalysis.dependencies && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Dependencies
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      p: 1.5,
                      bgcolor: 'rgba(244, 67, 54, 0.05)',
                      borderRadius: 1,
                      borderLeft: '3px solid',
                      borderColor: 'error.main'
                    }}
                    component="div"
                  >
                    {vulnerabilityAnalysis.dependencies}
                  </Typography>
                </Box>
              )}

              {vulnerabilityAnalysis.strategic_risks && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Strategic Risks
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      p: 1.5,
                      bgcolor: 'rgba(244, 67, 54, 0.05)',
                      borderRadius: 1,
                      borderLeft: '3px solid',
                      borderColor: 'error.main'
                    }}
                    component="div"
                  >
                    {vulnerabilityAnalysis.strategic_risks}
                  </Typography>
                </Box>
              )}

              {vulnerabilityAnalysis.defensive_posture && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Defensive Posture
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      p: 1.5,
                      bgcolor: 'rgba(244, 67, 54, 0.05)',
                      borderRadius: 1,
                      borderLeft: '3px solid',
                      borderColor: 'error.main'
                    }}
                    component="div"
                  >
                    {vulnerabilityAnalysis.defensive_posture}
                  </Typography>
                </Box>
              )}
            </InsightCard>
          </Grid>

          {/* Competitive Threats */}
          <Grid item xs={12} md={6}>
            <InsightCard title="Competitive Threats" icon={<AlertOutlined />} gradient="error">
              <List sx={{ pl: 0 }}>
                {competitiveThreats.map((threat, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      py: 0.75,
                      px: 0,
                      '&:not(:last-child)': {
                        borderBottom: '1px dashed',
                        borderColor: 'divider'
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <ArrowRightOutlined style={{ color: theme.palette.error.main }} />
                    </ListItemIcon>
                    <ListItemText primary={<Typography variant="body1">{threat}</Typography>} />
                  </ListItem>
                ))}
              </List>
            </InsightCard>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default StrategicInsights;
